import React, { useContext, useEffect, useMemo, useState } from 'react';
import useScrollLock from '@hooks/useScrollLock';
import { navigationMenuOptions } from '@constants/menu';
import { ANDROID_GUEST_DOWNLOAD_LINK, IOS_GUEST_DOWNLOAD_LINK } from '@constants/links';
import CloudImage from '@solar/common/Image/CloudImage';
import Link from 'next/link';
import { androidAppDownloadClick, iOSAppDownloadClick } from '@events/globals';
import Paragraph from '@solar/common/Paragraph/Paragraph';
import RoutingPath from '@constants/routingPath';
import CaptureEmailNavMenuItem from '@components/CaptureEmail/CaptureEmailNavMenuItem';
import { store } from '@context/store';
import { useEvents } from '@events/EventsProvider';
import { REFER_NAV_CLICKED } from '@constants/amplitudeEvents';
import TextLink from '@solar/common/Link/TextLink';
import { Menu } from './MenuComponents';

interface NavigationMenuProps {
  openCurrencySelectorModal: () => void;
  onClose: () => void;
  isVisible?: boolean;
}

export default function NavigationMenu({
  openCurrencySelectorModal,
  onClose,
  isVisible = true,
}: NavigationMenuProps) {
  const {
    state: { selectedCurrency },
  } = useContext(store);
  const { track } = useEvents();
  const [isAndroid, setIsAndroid] = useState(false);

  useScrollLock(isVisible);

  // Detect device type on client side only
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsAndroid(/(android)/i.test(navigator.userAgent));
    }
  }, []);

  // App download configuration based on device type
  const appDownloadConfig = useMemo(() => {
    if (isAndroid) {
      return {
        href: ANDROID_GUEST_DOWNLOAD_LINK,
        title: 'Download the Android app',
        alt: 'Google Play App Download',
        src: '/google-play-transparent.svg',
        width: 135,
        height: 40,
        onClick: androidAppDownloadClick,
      };
    }

    return {
      href: IOS_GUEST_DOWNLOAD_LINK,
      title: 'Download the iOS app',
      alt: 'Apple App Store App Download',
      src: '/app-store-transparent.svg',
      width: 120,
      height: 40,
      onClick: iOSAppDownloadClick,
    };
  }, [isAndroid]);

  const handleCurrencyClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    openCurrencySelectorModal();
  };

  const handleAppDownloadKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      appDownloadConfig.onClick();
    }
  };

  const handleMenuOptionClick = (option: { title: string }) => {
    if (option.title === 'Give $20, Get $20') {
      track(REFER_NAV_CLICKED, {});
    }

    onClose();
  };

  // Listen for when ESC key is pressed and then call the onClose function
  // Only add listener when menu is visible for performance
  useEffect(() => {
    if (!isVisible) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, isVisible]);

  return (
    <Menu onClose={onClose} isVisible={isVisible}>
      <Menu.Title>Unforgettable resorts and memories await</Menu.Title>
      <Menu.DefaultOptions>
        <CaptureEmailNavMenuItem />

        <TextLink
          href={RoutingPath.ABOUT_US}
          title="About ResortPass"
          className="py-spacing-sm desktop:py-0"
          size="md"
          onClick={onClose}
        >
          About ResortPass
        </TextLink>
      </Menu.DefaultOptions>
      <Menu.Options>
        {navigationMenuOptions.map((menuOption) => (
          <Menu.CollapsibleGroup
            key={menuOption.title}
            title={menuOption.title}
            initiallyExpanded={menuOption.title === 'Guests'}
          >
            {menuOption.title === 'Guests' && selectedCurrency && (
              <div className="flex items-center gap-spacing-sm py-spacing-sm desktop:py-0">
                <TextLink
                  href="#"
                  title="Currency"
                  onClick={(e) => handleCurrencyClick(e)}
                  size="md"
                >
                  Currency
                </TextLink>

                <span className="bg-solar-surface-secondary flex items-center justify-center px-spacing-sm py-spacing-xs rounded-solar-xxl">
                  <Paragraph size="xs">{`${selectedCurrency.symbol} ${selectedCurrency.iso_code}`}</Paragraph>
                </span>
              </div>
            )}

            {menuOption.options.map((option) => (
              <TextLink
                href={option.href}
                key={option.title}
                title={option.title}
                onClick={() => handleMenuOptionClick(option)}
                className="desktop:py-0 py-spacing-sm"
                size="md"
              >
                {option.title}
              </TextLink>
            ))}
          </Menu.CollapsibleGroup>
        ))}
      </Menu.Options>
      <Menu.AppDownload asChild>
        <Link
          href={appDownloadConfig.href}
          target="_blank"
          title={appDownloadConfig.title}
          rel="noreferrer"
          role="link"
          tabIndex={0}
          onClick={appDownloadConfig.onClick}
          onKeyDown={handleAppDownloadKeyDown}
        >
          <CloudImage
            alt={appDownloadConfig.alt}
            src={appDownloadConfig.src}
            bucket="static"
            width={appDownloadConfig.width}
            height={appDownloadConfig.height}
            quality={85}
          />
        </Link>
      </Menu.AppDownload>
    </Menu>
  );
}
